import { AssetStatus } from '@flatsy/types/Assets'
import { BookingStatus, CancelBy } from '@flatsy/types/Bookings'
import { parseApiDate } from '@flatsy/utils/Functions/DateTime/Date'
import { isPast } from 'date-fns'
import {
  ApplicationBookingState,
  BookingState,
  FrontApplicationBookingState,
} from '@flatsy/types/Applications/BookingStatus'
import { FrontApplicationState, State } from '@flatsy/types/Applications/Status'
import i18n from '@flatsy/utils/translations'
import { StatusProps } from './Type'

type StatusTextProps = Pick<
  StatusProps,
  'green' | 'red' | 'orange' | 'blue' | 'grey' | 'text' | 'hide'
>

export function getAssetStatusProps(
  status?: AssetStatus
): {
  green?: boolean
  red?: boolean
  orange?: boolean
  blue?: boolean
  text: string
} {
  if (status === AssetStatus.AVAILABLE) {
    return {
      green: true,
      text: i18n.t('available'),
    }
  }
  if (status === AssetStatus.STANDBY) {
    return {
      orange: true,
      text: i18n.t('stand_by'),
    }
  }
  if (status === AssetStatus.CLOSED) {
    return {
      red: true,
      text: i18n.t('closed'),
    }
  }
  return {
    text: '',
  }
}

export function getBookingStatusProps(
  status?: BookingStatus | string,
  date?: string
): {
  green?: boolean
  red?: boolean
  orange?: boolean
  blue?: boolean
  text: string
} {
  if (status === BookingStatus.ACCEPTED) {
    if (date && isPast(parseApiDate(date))) {
      return {
        green: true,
        text: i18n.t('done'),
      }
    }
    return {
      blue: true,
      text: i18n.t('confirmed'),
    }
  }
  if (status === CancelBy.RESCHEDULING || status === CancelBy.MOVING) {
    return {
      orange: true,
      text: i18n.t('rescheduled'),
    }
  }
  return {
    red: true,
    text: i18n.t('canceled'),
  }
}

export function getApplicationBookingStatusProps({
  state,
  bookingState,
  omitWaitingList,
}: {
  state?: State
  bookingState?: BookingState
  omitWaitingList?: boolean
}): {
  green?: boolean
  red?: boolean
  orange?: boolean
  blue?: boolean
  grey?: boolean
  text: string
} {
  if (bookingState === ApplicationBookingState.CONFIRMED) {
    return {
      blue: true,
      text: i18n.t('confirmed'),
    }
  }
  if (bookingState === ApplicationBookingState.COMPLETED) {
    return {
      green: true,
      text: i18n.t('done'),
    }
  }
  if (bookingState === FrontApplicationBookingState.RESCHEDULED) {
    return {
      orange: true,
      text: i18n.t('moved'),
    }
  }
  if (!omitWaitingList && state === FrontApplicationState.WAITING_LIST) {
    return {
      orange: true,
      text: i18n.t('waiting_list'),
    }
  }
  if (bookingState === FrontApplicationBookingState.CANCELLED) {
    return {
      red: true,
      text: i18n.t('canceled'),
    }
  }
  return {
    grey: true,
    text: i18n.t('invited'),
  }
}

export function getQualificationStatusProps(
  isDefault?: boolean
): {
  green?: boolean
  red?: boolean
  orange?: boolean
  blue?: boolean
  hide?: boolean
  text: string
} {
  if (isDefault) {
    return {
      blue: true,
      text: i18n.t('by_default'),
    }
  }
  return {
    hide: true,
    text: i18n.t('by_default'),
  }
}

export function getScoredBasedStatusProps(
  score: number,
  thresholds: {
    average: number
    good: number
  }
): StatusTextProps {
  const text = score.toFixed(1)
  if (score > thresholds.good) {
    return {
      text,
      green: true,
    }
  }
  if (score > thresholds.average) {
    return {
      text,
      orange: true,
    }
  }
  return {
    text,
    red: true,
  }
}
